import { Component, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import type { ErrorCode } from '@libs/errors/error-codes';
import { AuthService } from '@services/auth/auth.service';
import { MfaService } from '@services/auth/mfa.service';
import { AuthFormComponent } from '@shared/components/auth-form/auth-form.component';
import { AuthModalService } from '@shared/services/auth-modal.service';

@Component({
	selector: 'app-auth-modal',
	standalone: true,
	imports: [AuthFormComponent, TranslocoPipe],
	templateUrl: './auth-modal.component.html',
	styleUrl: './auth-modal.component.scss',
})
export class AuthModalComponent {
	private authModalService = inject(AuthModalService);
	private authService = inject(AuthService);
	private mfaService = inject(MfaService);
	private translocoService = inject(TranslocoService);
	private router = inject(Router);

	isOpen = this.authModalService.isOpen;
	mode = this.authModalService.mode;

	currentMode: 1 | 2 | 3 | 4 | 5 = 1;
	errorMessage: string | null = null;
	isLoading = false;
	showSuccessState = false;

	// MFA state
	mfaAccessToken: string | null = null;
	mfaFactorId: string | null = null;

	constructor() {
		// Sync mode with service
		effect(() => {
			this.currentMode = this.mode();
			this.resetState();
		});
	}

	onClose(): void {
		if (!this.isLoading) {
			this.authModalService.close();
			this.resetState();
		}
	}

	onBackdropClick(event: MouseEvent): void {
		if (event.target === event.currentTarget) {
			this.onClose();
		}
	}

	private resetState(): void {
		this.errorMessage = null;
		this.showSuccessState = false;
		this.mfaAccessToken = null;
		this.mfaFactorId = null;
	}

	onFormSubmit(formData: {
		email?: string;
		password?: string;
		newPassword?: string;
		confirmPassword?: string;
		mfaCode?: string;
		trustDevice?: boolean;
	}): void {
		this.errorMessage = null;

		switch (this.currentMode) {
			case 1:
				this.handleLogin(formData);
				break;
			case 2:
				this.handleResetPassword(formData);
				break;
			case 3:
				this.handleResetPasswordConfirm(formData);
				break;
			case 4:
				this.handleMFAVerification(formData);
				break;
			case 5:
				this.handleRegister(formData);
				break;
		}
	}

	private handleLogin(formData: {
		email?: string;
		password?: string;
		mfaCode?: string;
		trustDevice?: boolean;
	}): void {
		if (!formData.email || !formData.password) {
			this.errorMessage = this.translocoService.translate('auth.errors.emailPasswordRequired');
			return;
		}

		this.isLoading = true;
		this.authService.login(formData.email, formData.password).subscribe({
			next: (response) => {
				this.isLoading = false;
				if (response.mfa_required) {
					this.currentMode = 4; // Switch to MFA mode
					this.mfaAccessToken = response.access_token;
					this.mfaFactorId = response.factor_id;
				} else {
					// If successful and no MFA, authService handles navigation
					// Close modal immediately
					this.authModalService.close();
				}
			},
			error: (err: Error) => {
				this.isLoading = false;
				this.errorMessage = err.message || 'Ocurrió un error desconocido.';
			},
		});
	}

	private handleRegister(formData: { email?: string; password?: string }): void {
		if (!formData.email || !formData.password) {
			this.errorMessage = this.translocoService.translate('auth.errors.requiredFields');
			return;
		}

		this.isLoading = true;
		this.authService
			.register({
				email: formData.email,
				password: formData.password,
			})
			.subscribe({
				next: () => {
					this.isLoading = false;
					// AuthService handles auto-login and navigation to /boost
					// Close modal immediately
					this.authModalService.close();
				},
				error: (err: Error & { code?: ErrorCode }) => {
					this.isLoading = false;
					// Account created but not logged in: AuthService already said so and goes to /login
					if (err.code === 'REGISTERED_LOGIN_FAILED') {
						this.authModalService.close();
						return;
					}
					this.errorMessage = err.message || this.translocoService.translate('auth.errors.unknownError');
				},
			});
	}

	private handleResetPassword(formData: { email?: string }): void {
		if (!formData.email) {
			this.errorMessage = this.translocoService.translate('auth.errors.required');
			return;
		}

		this.isLoading = true;
		this.authService.resetPassword(formData.email).subscribe({
			next: () => {
				this.isLoading = false;
				this.showSuccessState = true;
				setTimeout(() => {
					this.authModalService.close();
				}, 2000);
			},
			error: (err: Error) => {
				this.isLoading = false;
				this.errorMessage = err.message || this.translocoService.translate('authMessages.passwordResetError');
			},
		});
	}

	private handleResetPasswordConfirm(formData: { newPassword?: string; confirmPassword?: string }): void {
		if (!formData.newPassword || !formData.confirmPassword) {
			this.errorMessage = this.translocoService.translate('auth.errors.required');
			return;
		}

		// This would need token from URL
		// For now, just close modal
		this.authModalService.close();
	}

	private handleMFAVerification(formData: { mfaCode?: string; trustDevice?: boolean }): void {
		if (!formData.mfaCode || !this.mfaAccessToken || !this.mfaFactorId) {
			this.errorMessage = this.translocoService.translate('auth.login.mfa.invalidCode');
			return;
		}

		this.isLoading = true;
		this.mfaService
			.verifyLogin(this.mfaAccessToken, this.mfaFactorId, formData.mfaCode, formData.trustDevice || false)
			.subscribe({
				next: () => {
					this.isLoading = false;
					this.authModalService.close();
					this.resetState();
				},
				error: (err: { code?: ErrorCode; message?: string }) => {
					this.isLoading = false;
					// Too many attempts is not a wrong code: say so (translated by the interceptor)
					this.errorMessage =
						err?.code === 'TOO_MANY_REQUESTS' && err.message
							? err.message
							: this.translocoService.translate('auth.login.mfa.invalidCode');
				},
			});
	}

	cancelMFA(): void {
		this.currentMode = 1;
		this.mfaAccessToken = null;
		this.mfaFactorId = null;
		this.errorMessage = null;
	}

	onGoogleLogin(): void {
		// TODO: Implement Google OAuth login
	}

	onAppleLogin(): void {
		// TODO: Implement Apple OAuth login
	}

	switchToLogin(): void {
		this.authModalService.openLogin();
	}

	switchToRegister(): void {
		this.authModalService.openRegister();
	}

	switchToResetPassword(): void {
		this.authModalService.openResetPassword();
	}
}
